import { useState, useEffect, useRef } from 'react';

/**
 * Enhanced counter animation with color flash on value change
 * @param {number} target - Target value
 * @param {boolean} enabled - Enable animation
 * @returns {object} - { value, flashColor }
 */
export const useEnhancedCounter = (target, enabled = true) => {
  const [current, setCurrent] = useState(enabled ? 0 : target);
  const [prevValue, setPrevValue] = useState(target);
  const [flashColor, setFlashColor] = useState(null);
  const animationRef = useRef(null);

  useEffect(() => {
    if (!enabled) {
      setCurrent(target);
      return;
    }

    // Detect value change direction for color flash
    if (target > prevValue && prevValue !== 0) {
      setFlashColor('green');
      setTimeout(() => setFlashColor(null), 600);
    } else if (target < prevValue && prevValue !== 0) {
      setFlashColor('red');
      setTimeout(() => setFlashColor(null), 600);
    }

    setPrevValue(target);

    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    const startValue = current;
    const difference = target - startValue;
    const duration = 1000;
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Ease out cubic
      const easeProgress = 1 - Math.pow(1 - progress, 3);
      
      const nextValue = startValue + (difference * easeProgress);
      setCurrent(nextValue);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setCurrent(target);
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [target, enabled]);

  return { value: current, flashColor };
};

/**
 * CSS class helper for flash color
 */
export const getFlashClass = (flashColor) => {
  if (!flashColor) return '';
  return `flash-${flashColor}`;
};
